import {
  fetchLatestPodcastEpisodes,
  fetchPodcastEpisode,
  fetchPodcastEpisodesPaginated,
  fetchPodcastShow,
  type PodcastsFetchPolicy,
  type PaginatedPodcastEpisodesResult,
} from '@core/services/podcasts/podcasts-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { PodcastEpisode, PodcastShow } from '@domain/models/podcast'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'

export const PROF_BAS_BLOEM_WEEKLY_SHOW_ID = '5FVqV671ESQ5VxbncsIxIS'

export class PodcastsFeature {
  public static async getPodcastShow(showId: string): Promise<PodcastShow> {
    try {
      return await fetchPodcastShow(showId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch podcast show "${showId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getLatestPodcastEpisodes(
    showId: string,
    count = 3,
    fetchPolicy: PodcastsFetchPolicy = 'default',
  ): Promise<PodcastEpisode[]> {
    try {
      return await fetchLatestPodcastEpisodes(showId, count, fetchPolicy)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch latest podcast episodes for show "${showId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getPodcastEpisode(episodeId: string): Promise<PodcastEpisode> {
    try {
      return await fetchPodcastEpisode(episodeId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch podcast episode "${episodeId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getPodcastEpisodesPaginated(
    showId: string,
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
    fetchPolicy: PodcastsFetchPolicy = 'default',
  ): Promise<PaginatedPodcastEpisodesResult> {
    try {
      return await fetchPodcastEpisodesPaginated(
        showId,
        pageSize,
        lastDocument,
        fetchPolicy,
      )
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch paginated podcast episodes for show "${showId}".`,
        underlyingException: error,
      })
    }
  }
}

export async function getPodcastShow(showId: string): Promise<PodcastShow> {
  return PodcastsFeature.getPodcastShow(showId)
}

export async function getLatestPodcastEpisodes(
  showId: string,
  count = 3,
  fetchPolicy: PodcastsFetchPolicy = 'default',
): Promise<PodcastEpisode[]> {
  return PodcastsFeature.getLatestPodcastEpisodes(showId, count, fetchPolicy)
}

export async function getPodcastEpisode(episodeId: string): Promise<PodcastEpisode> {
  return PodcastsFeature.getPodcastEpisode(episodeId)
}

export async function getPodcastEpisodesPaginated(
  showId: string,
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
  fetchPolicy: PodcastsFetchPolicy = 'default',
): Promise<PaginatedPodcastEpisodesResult> {
  return PodcastsFeature.getPodcastEpisodesPaginated(
    showId,
    pageSize,
    lastDocument,
    fetchPolicy,
  )
}

export type { PaginatedPodcastEpisodesResult }
